// components/BatchProcessing/BatchItemCard.jsx
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Collapse,
  CardActions,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

const BatchItemCard = ({ 
  item, 
  index, 
  onRemove, 
  onFileUpload, 
  onParamUpdate, 
  onRemoveFile, 
  isValid, 
  disabled 
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(false);

  const processingMethods = [
    { value: 'METODO_3_COMPLETO', label: 'Método 3 Completo' },
    { value: 'TRADICIONAL', label: 'Método Alternativo' },
    { value: 'NOVO', label: 'Novo Método' }
  ];

  const crsOptions = [
    'EPSG:31982',
    'EPSG:31983', 
    'EPSG:31984',
    'EPSG:31985',
    'EPSG:4326',
    'EPSG:3857'
  ];

  const directionOptions = [
    { value: 'AUTOMATICO', label: 'Automático' },
    { value: 'CRESCENTE', label: 'Crescente' },
    { value: 'DECRESCENTE', label: 'Decrescente' }
  ];

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        position: 'relative',
        borderColor: isValid ? 'success.main' : 'grey.400',
        opacity: disabled ? 0.6 : 1
      }}
    >
      {/* Cabeçalho do Card - Sempre visível */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        p: { xs: 1.5, sm: 2, md: 2.5 },
        borderBottom: expanded ? '1px solid' : 'none',
        borderColor: 'divider',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' }
      }}
      onClick={handleToggleExpand}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleExpand();
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="h6" component="h3">
            Conjunto {index + 1}
          </Typography>
          <Chip 
            label={isValid ? "Pronto" : "Incompleto"} 
            size="small"
            color={isValid ? "success" : "default"}
            variant={isValid ? "filled" : "outlined"}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Resumo quando minimizado */}
          {/* {!expanded && (
            <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
              {item.axisFileName ? item.axisFileName : 'Sem eixo'} • 
              {item.imgFileName ? item.imgFileName : ' Sem imagens'} •
              {item.processingMethod}
            </Typography>
          )} */}
          
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item.id);
            }}
            disabled={disabled}
            color="error"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Conteúdo expansível */}
      <Collapse in={expanded}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Grid container spacing={2}>
            {/* COLUNA 1: Arquivos */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Arquivos do Conjunto
              </Typography>
              
              {/* Arquivo do Eixo */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                  Arquivo do Eixo Rodoviário (.gpkg) *
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    size="small"
                    disabled={disabled}
                    sx={{ flex: 1 }}
                  >
                    {item.axisFileName || 'Selecionar eixo'}
                    <input
                      type="file"
                      accept=".gpkg"
                      hidden
                      onChange={(e) => onFileUpload(e, 'axisFile', item.id)}
                    />
                  </Button>
                  {item.axisFileName && (
                    <IconButton 
                      size="small" 
                      onClick={() => onRemoveFile('axisFile')}
                      disabled={disabled}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Arquivo de Imagens */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                  Arquivo de Imagens/Fotos (.gpkg) *
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    size="small"
                    disabled={disabled}
                    sx={{ flex: 1 }}
                  >
                    {item.imgFileName || 'Selecionar imagens'}
                    <input
                      type="file"
                      accept=".gpkg"
                      hidden
                      onChange={(e) => onFileUpload(e, 'imgFile', item.id)}
                    />
                  </Button>
                  {item.imgFileName && (
                    <IconButton 
                      size="small" 
                      onClick={() => onRemoveFile('imgFile')}
                      disabled={disabled}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Arquivo de Estaqueamento */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                  Arquivo de Estaqueamento (.kmz ou .gpkg)
                  {item.processingMethod === 'NOVO' && ' (Não necessário)'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    size="small"
                    disabled={disabled || item.processingMethod === 'NOVO'}
                    sx={{ 
                      flex: 1,
                      opacity: item.processingMethod === 'NOVO' ? 0.6 : 1
                    }}
                  >
                    {item.stakeFileName || 'Selecionar estaqueamento'}
                    <input
                      type="file"
                      accept=".kmz,.gpkg"
                      hidden
                      onChange={(e) => onFileUpload(e, 'stakeFile', item.id)}
                      disabled={item.processingMethod === 'NOVO'}
                    />
                  </Button>
                  {item.stakeFileName && (
                    <IconButton 
                      size="small" 
                      onClick={() => onRemoveFile('stakeFile')}
                      disabled={disabled}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                </Box>
                {item.processingMethod === 'NOVO' && (
                  <Typography variant="caption" color="textSecondary">
                    O estaqueamento será gerado automaticamente
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* COLUNA 2: Parâmetros */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="primary" mb={2}>
                Parâmetros de Processamento
              </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nome da Via"
                    value={item.taskName || ''}
                    onChange={(e) => onParamUpdate(item.id, 'taskName', e.target.value)}
                    disabled={disabled}
                    sx={{ mb: 2 }}
                  />
              {/* Método de Processamento */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Método de Processamento</InputLabel>
                <Select
                  value={item.processingMethod}
                  label="Método de Processamento"
                  onChange={(e) => onParamUpdate(item.id, 'processingMethod', e.target.value)}
                  disabled={disabled}
                >
                  {processingMethods.map(method => (
                    <MenuItem key={method.value} value={method.value}>
                      {method.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Direção do Eixo */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Direção do Eixo</InputLabel>
                <Select
                  value={item.eixoDirection}
                  label="Direção do Eixo"
                  onChange={(e) => onParamUpdate(item.id, 'eixoDirection', e.target.value)}
                  disabled={disabled}
                >
                  {directionOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Sistema de Coordenadas */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Sistema de Coordenadas</InputLabel>
                <Select
                  value={item.crs}
                  label="Sistema de Coordenadas"
                  onChange={(e) => onParamUpdate(item.id, 'crs', e.target.value)}
                  disabled={disabled}
                >
                  {crsOptions.map(crs => (
                    <MenuItem key={crs} value={crs}>
                      {crs}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Campos condicionais para método tradicional */}
              {(item.processingMethod === 'TRADICIONAL') && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    label="Estaca Inicial"
                    value={item.startPointLabel}
                    onChange={(e) => onParamUpdate(item.id, 'startPointLabel', e.target.value)}
                    disabled={disabled}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Nome da Coluna da Estaca"
                    value={item.startNameColumn}
                    onChange={(e) => onParamUpdate(item.id, 'startNameColumn', e.target.value)}
                    disabled={disabled}
                  />
                </>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default BatchItemCard;